import React, { useState } from 'react';

import { EyeOutlined } from '@ant-design/icons';
import { PagePropsWithId } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { ShowItem } from '@fastlessjs/backoffice-layer/dist/components/templates';
import {
  APIOptions,
  currentOrganization,
  currentUser,
  Enterprise,
  useMultiTenancy,
} from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { useQueryClient } from '@tanstack/react-query';
import { Col, Row, Space, Tag, Tooltip } from 'antd';
import { navigate } from 'hookrouter-react';

import { OrderReturnModal } from './OrderReturnModal';
import ViewOrder from './ViewOrder';
import EurekaRefreshButton from '../../components/EurekaRefreshButton';
import { t } from '../../config/translations';
import { useSuiviOrder } from '../../trace/hooks';
import { useOrder } from '../hooks';
import { orderSchema } from '../schemas';
import { Order } from '../types';

// Register this component for debug tracking
debug.registerComponent('OrderDetail');

const Detail = ({ id, org }: PagePropsWithId) => {
  debug.trackRender('OrderDetail');
  debug.log('OrderDetail', `Rendering with id: ${id}, org: ${org}`);
  const queryClient = useQueryClient();
  const { getPage, setPage } = useMultiTenancy();
  const enterpriseId = getPage('enterprises')?.[org]?.enterpriseId;
  const [orderOptions, setOrderOptions] = useState<APIOptions>({
    orderId: id,
    clientId: enterpriseId,
  });

  const { data, isLoading, isFetching } = useOrder(orderOptions);
  const { data: suivi, isLoading: isSuiviLoading } = useSuiviOrder({
    tenantId: currentUser()?.user?.id || '',
    orderId: orderOptions.orderId,
    clientId: orderOptions.clientId,
  });

  const handleSubmit = async (orderId: string, clientId?: string) => {
    debug.log('OrderDetail', `Navigating to order: ${orderId} for client: ${clientId}`);
    const eLabel = await queryClient
      .getQueryData<any>(['enterprises', 'select', org])
      .pages[0].data.find((e: Enterprise) => e.enterpriseId.toString() === clientId)
      .enterpriseName;
    await setPage({ [org]: { enterpriseId: clientId, label: eLabel } }, 'enterprises');
    setOrderOptions({ orderId, clientId });
    navigate(`/${org}/orders/${orderId}/view`);
  };

  return (
    <>
      <ShowItem<Order>
        org={org!}
        dataSource={data!}
        id={id!}
        schema={orderSchema}
        nameKey="ndoc"
        path="orders"
        title={t('page.order')}
        isFetching={isFetching}
        isLoading={isLoading || isSuiviLoading}
        hasEdit={false}
        headerExtraActions={
          <Space wrap size="small">
            <EurekaRefreshButton
              entity="order"
              items={[
                {
                  orderId: String(orderOptions.orderId ?? id ?? ''),
                  enterpriseId: Number(orderOptions.clientId),
                },
              ]}
              queryKeysToInvalidate={[
                ['orders', orderOptions.orderId ?? id, orderOptions.clientId],
              ]}
              disabled={
                !orderOptions.orderId ||
                orderOptions.clientId === undefined ||
                orderOptions.clientId === null ||
                !Number.isFinite(Number(orderOptions.clientId))
              }
            />
            {suivi?.suiviId && (
              <Tooltip title={t('order.viewTracking')}>
                <Tag
                  color="blue"
                  icon={<EyeOutlined />}
                  style={{ marginRight: '12px', cursor: 'pointer' }}
                  data-navigate-path={`/${currentOrganization()?.organizationId}/suivi/${
                    suivi.suiviId
                  }/view`}
                >
                  {t('order.trackingExists')}
                </Tag>
              </Tooltip>
            )}
            <OrderReturnModal handleSubmit={handleSubmit} />
          </Space>
        }
      />
      <Row justify="start" gutter={24} wrap>
        <Col xs={24}>
          <ViewOrder
            clientId={orderOptions.clientId}
            orderId={orderOptions.orderId}
            org={org}
            hideInfo
            isSuiviLoading={isSuiviLoading}
            suiviId={suivi?.suiviId}
          />
        </Col>
      </Row>
    </>
  );
};

export default Detail;

// Mark this component as initialized
debug.markInitialized('OrderDetail');
